import moment from 'moment';
import { DeliveryBriefMetric } from 'core/report/DeliveryBriefReport';

/** 畫面上四欄由上而下的排列，依設計稿。 */
export const DELIVERY_BRIEF_METRIC_COLUMNS: DeliveryBriefMetric[][] = [
  [
    DeliveryBriefMetric.CPC,
    DeliveryBriefMetric.IMPRESS,
    DeliveryBriefMetric.VIEW,
    DeliveryBriefMetric.CONVS,
    DeliveryBriefMetric.CTR
  ],
  [
    DeliveryBriefMetric.CPM,
    DeliveryBriefMetric.CLICK,
    DeliveryBriefMetric.VIEW_RATE
  ],
  [DeliveryBriefMetric.CPV],
  [DeliveryBriefMetric.CPA]
];

/** 送出的順序固定為 enum 宣告的順序，與勾選先後無關。 */
const METRIC_ORDER = Object.values(DeliveryBriefMetric);

export enum DeliveryBriefPreset {
  CPM = 'cpm',
  CPC = 'cpc',
  VIDEO = 'video',
  OUTDOOR = 'outdoor'
}

export const DELIVERY_BRIEF_PRESET_METRICS: {
  [preset in DeliveryBriefPreset]: DeliveryBriefMetric[];
} = {
  [DeliveryBriefPreset.CPM]: [
    DeliveryBriefMetric.CPM,
    DeliveryBriefMetric.IMPRESS,
    DeliveryBriefMetric.CLICK,
    DeliveryBriefMetric.CTR
  ],
  [DeliveryBriefPreset.CPC]: [
    DeliveryBriefMetric.CPC,
    DeliveryBriefMetric.IMPRESS,
    DeliveryBriefMetric.CLICK,
    DeliveryBriefMetric.CTR
  ],
  [DeliveryBriefPreset.VIDEO]: [
    DeliveryBriefMetric.CPV,
    DeliveryBriefMetric.IMPRESS,
    DeliveryBriefMetric.VIEW,
    DeliveryBriefMetric.VIEW_RATE,
    DeliveryBriefMetric.CLICK,
    DeliveryBriefMetric.CTR
  ],
  [DeliveryBriefPreset.OUTDOOR]: [
    DeliveryBriefMetric.CPM,
    DeliveryBriefMetric.IMPRESS,
    DeliveryBriefMetric.VIEW,
    DeliveryBriefMetric.VIEW_RATE
  ]
};

/**
 * 勾了其中一個，另一個跟著勾；取消也一樣。
 * 數量與它的比率在報表上是成對呈現的。
 */
const LINKED_METRICS: { [metric: string]: DeliveryBriefMetric } = {
  [DeliveryBriefMetric.VIEW]: DeliveryBriefMetric.VIEW_RATE,
  [DeliveryBriefMetric.VIEW_RATE]: DeliveryBriefMetric.VIEW,
  [DeliveryBriefMetric.CLICK]: DeliveryBriefMetric.CTR,
  [DeliveryBriefMetric.CTR]: DeliveryBriefMetric.CLICK
};

export type DeliveryBriefSelection = {
  readonly checked: DeliveryBriefMetric[];
  /** 目前高亮的快選；手動改過任何一格就不再高亮。 */
  readonly activePreset?: DeliveryBriefPreset;
};

export const initialDeliveryBriefSelection = (): DeliveryBriefSelection => ({
  checked: []
});

/** 套用快選：整組換成該組欄位，與走期自訂報表的快選相同，其餘欄位仍可自行勾選。 */
export const applyDeliveryBriefPreset = (
  preset: DeliveryBriefPreset
): DeliveryBriefSelection => {
  const metrics = DELIVERY_BRIEF_PRESET_METRICS[preset];
  return {
    checked: METRIC_ORDER.filter((metric) => metrics.includes(metric)),
    activePreset: preset
  };
};

/** 手動切換一格，成對的另一格一起切換。 */
export const toggleDeliveryBriefMetric = (
  selection: DeliveryBriefSelection,
  metric: DeliveryBriefMetric,
  checked: boolean
): DeliveryBriefSelection => {
  const linked = LINKED_METRICS[metric];
  const targets = linked ? [metric, linked] : [metric];
  const nextChecked = checked
    ? [...selection.checked, ...targets]
    : selection.checked.filter((item) => !targets.includes(item));
  return {
    checked: METRIC_ORDER.filter((item) => nextChecked.includes(item)),
    activePreset: undefined
  };
};

/** 送給後端的 metrics，依固定順序。 */
export const serializeDeliveryBriefMetrics = (
  checked: DeliveryBriefMetric[]
): DeliveryBriefMetric[] =>
  METRIC_ORDER.filter((metric) => checked.includes(metric));

/** 報表區間必須完整落在訂單走期內，以日為單位比較。 */
export const isWithinOrderPeriod = (
  startDate: string,
  endDate: string,
  orderStartDate: string,
  orderEndDate: string
): boolean => {
  if (!startDate || !endDate) {
    return false;
  }
  return (
    !moment(startDate).isBefore(moment(orderStartDate), 'day') &&
    !moment(endDate).isAfter(moment(orderEndDate), 'day')
  );
};

export const canDownloadDeliveryBrief = (
  selection: DeliveryBriefSelection,
  dateRangeValid: boolean
): boolean => dateRangeValid && selection.checked.length > 0;

/**
 * 勾選框的文字。兩個「平均」比率的字樣和走期自訂報表不同，
 * 所以另外有 key；其餘沿用同一組。
 */
export const DELIVERY_BRIEF_METRIC_LABEL_KEYS: {
  [metric in DeliveryBriefMetric]: string;
} = {
  [DeliveryBriefMetric.CPC]: 'downloadReportPanel.labels.cpc',
  [DeliveryBriefMetric.CPM]: 'downloadReportPanel.labels.cpm',
  [DeliveryBriefMetric.CPV]: 'downloadReportPanel.labels.cpv',
  [DeliveryBriefMetric.CPA]: 'downloadReportPanel.labels.cpa',
  [DeliveryBriefMetric.IMPRESS]: 'downloadReportPanel.labels.impress',
  [DeliveryBriefMetric.VIEW]: 'downloadReportPanel.labels.adView',
  [DeliveryBriefMetric.CLICK]: 'downloadReportPanel.labels.click',
  [DeliveryBriefMetric.CONVS]: 'downloadReportPanel.labels.convs',
  [DeliveryBriefMetric.VIEW_RATE]: 'downloadReportPanel.labels.averageViewRate',
  [DeliveryBriefMetric.CTR]: 'downloadReportPanel.labels.averageCtr'
};

export const DELIVERY_BRIEF_PRESET_LABEL_KEYS: {
  [preset in DeliveryBriefPreset]: string;
} = {
  [DeliveryBriefPreset.CPM]: 'downloadReportPanel.labels.cpmOption',
  [DeliveryBriefPreset.CPC]: 'downloadReportPanel.labels.cpcOption',
  [DeliveryBriefPreset.VIDEO]: 'downloadReportPanel.labels.videoOption',
  [DeliveryBriefPreset.OUTDOOR]: 'downloadReportPanel.labels.outdoorOption'
};
